import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/salon/Navbar';
import { Card } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

const API = '/api';

const GalleryPage = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery images', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (image.image_url?.startsWith('/uploads')) {
      return `/api${image.image_url}`;
    }
    return image.image_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">
            Unsere Galerie
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Entdecken Sie Einblicke in unseren Salon und unsere kreativen Arbeiten
          </p>
        </div>

        {galleryImages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">Noch keine Bilder in der Galerie</p>
          </div>
        ) : (
          <>
            {/* Carousel - Show first 6 images */}
            {galleryImages.length >= 3 && (
              <div className="mb-16">
                <Carousel className="max-w-5xl mx-auto" data-testid="gallery-carousel">
                  <CarouselContent>
                    {galleryImages.slice(0, 6).map((image, idx) => (
                      <CarouselItem key={image.id || idx}>
                        <Card className="overflow-hidden border-slate-100">
                          <img
                            src={getImageUrl(image)}
                            alt={image.title || `Galerie Bild ${idx + 1}`}
                            className="w-full h-[500px] object-cover"
                          />
                        </Card>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>
            )}

            {/* Grid - Show all images */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((image, idx) => (
                <div
                  key={image.id || idx}
                  className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
                  data-testid={`gallery-image-${idx}`}
                  onClick={() => setLightboxIdx(idx)}
                >
                  <img
                    src={getImageUrl(image)}
                    alt={image.title || `Galerie Bild ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {image.title && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-white font-medium">{image.title}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10"
            data-testid="lightbox-close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + galleryImages.length) % galleryImages.length); }}
                className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors p-2"
                data-testid="lightbox-prev"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % galleryImages.length); }}
                className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors p-2"
                data-testid="lightbox-next"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          <img
            src={getImageUrl(galleryImages[lightboxIdx])}
            alt={galleryImages[lightboxIdx]?.title || 'Galerie Bild'}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          <div className="absolute bottom-6 text-white/60 text-sm">
            {lightboxIdx + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
